import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Clock,
  Receipt,
  Plus,
  CircleNotch,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RowActions } from '@/components/shared/RowActions';
import {
  fetchActivities,
  fetchActivityTotals,
  fetchActivityCategories,
  deleteActivity,
  type Activity,
  type ActivityType,
  type ActivityInvoiceStatus,
} from '@/lib/activitiesApi';
import { fetchUsers } from '@/lib/usersApi';
import { useAuthStore } from '@/features/auth/store';
import { ActivityModal } from '@/components/activities/ActivityModal';
import { normaliseCategory } from '@jurivoxa/shared';
import { canLogActivity as canLogActivityFor } from '@/lib/activityAccess';
import { getApiErrorMessage, isForbidden } from '@/lib/api';
import { BackButton } from '@/components/shared/BackButton';

const PAGE_LIMIT = 50;

function fmtMoney(value: number, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function fmtDuration(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function statusBadgeClass(s: ActivityInvoiceStatus): string {
  switch (s) {
    case 'unbilled': return 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30';
    case 'draft':    return 'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/30';
    case 'billed':   return 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30';
    case 'non_billable': return 'bg-zinc-100 text-zinc-700 dark:text-zinc-300 border-zinc-200';
  }
}

function statusLabel(s: ActivityInvoiceStatus): string {
  switch (s) {
    case 'unbilled': return 'Unbilled';
    case 'draft': return 'Draft';
    case 'billed': return 'Billed';
    case 'non_billable': return 'Non-billable';
  }
}

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  // Barristers must only ever see their own entries — confidentiality requirement.
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(currentUser?.memberType ?? '');
  const canLogActivity = canLogActivityFor(currentUser?.memberType);

  // ─── Filter state ───
  const [type, setType] = useState<ActivityType | undefined>();
  const [invoiceStatus, setInvoiceStatus] = useState<ActivityInvoiceStatus | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [firmUserId, setFirmUserId] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState<ActivityType | null>(null);
  const [editTarget, setEditTarget] = useState<Activity | null>(null);
  // Reading an entry is not editing it. Edit is refused on a billed entry and on another
  // person's, and it was the only way to open one — so the notes and rate behind a billed item
  // could be seen nowhere at all. That is the entry a client queries.
  const [viewTarget, setViewTarget] = useState<Activity | null>(null);

  // Email → billing bridge: /…/activities?matterId=…&source=email opens the
  // time-entry modal pre-filled with that matter, so a barrister captures
  // billable time straight from a communication. The captured time then rolls
  // into the matter's activities and the barrister's total fees.
  const [searchParams, setSearchParams] = useSearchParams();
  const emailMatterId = searchParams.get('source') === 'email' ? searchParams.get('matterId') : null;
  useEffect(() => {
    if (emailMatterId) setCreateOpen('time');
  }, [emailMatterId]);
  function clearEmailParams() {
    if (emailMatterId) setSearchParams({}, { replace: true });
  }

  // For barristers: lock the user filter to their own ID regardless of UI state.
  const effectiveFirmUserId = isBarrister ? currentUser?.id : firmUserId;

  const queryParams = useMemo(
    () => ({
      type,
      invoiceStatus,
      category,
      firmUserId: effectiveFirmUserId,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      page,
      limit: PAGE_LIMIT,
    }),
    [type, invoiceStatus, category, effectiveFirmUserId, dateFrom, dateTo, page],
  );

  const listQ = useQuery({
    queryKey: ['activities', queryParams],
    queryFn: () => fetchActivities(queryParams),
  });

  // The totals row uses the SAME filters as the list (the server shares one WHERE for both), so
  // the tiles answer "what did this category cost me" rather than summarising rows not on screen.
  const totalsQ = useQuery({
    queryKey: ['activity-totals', { type, invoiceStatus, category, effectiveFirmUserId, dateFrom, dateTo }],
    queryFn: () =>
      fetchActivityTotals({
        type,
        invoiceStatus,
        category,
        firmUserId: effectiveFirmUserId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
  });

  // The categories this practice has actually used — the same list the entry picker offers, so
  // the filter can only name a category that exists. Follows the Type filter: "All" merges both
  // vocabularies, because a time category and an expense category are different lists.
  const categoriesQ = useQuery({
    queryKey: ['activity-categories', type ?? 'all'],
    queryFn: () => fetchActivityCategories(type),
    staleTime: 60_000,
  });

  // Keep a chosen category selectable after a Type change drops it from the list, so the filter
  // can never sit applied with nothing in the dropdown to show for it.
  const categoryOptions = useMemo(() => {
    const used = categoriesQ.data ?? [];
    return category && !used.includes(category) ? [category, ...used] : used;
  }, [categoriesQ.data, category]);

  const usersQ = useQuery({
    queryKey: ['users', 'firm-users-active'],
    queryFn: () => fetchUsers({ status: 'active', limit: 100 }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteActivity(id),
    onSuccess: () => {
      toast.success('Activity deleted');
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: ['activity-totals'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete activity')),
  });

  const clearFilters = () => {
    setType(undefined);
    setInvoiceStatus(undefined);
    setCategory(undefined);
    setFirmUserId(undefined);
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasFilters = Boolean(type || invoiceStatus || category || firmUserId || dateFrom || dateTo);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <BackButton onClick={() => navigate(-1)} />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Time &amp; Expenses
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {canLogActivity
              ? 'Log billable time and expenses against matters before they roll into a fee note.'
              : // A pupil can read this ledger but not write to it, so telling them to "log"
                // something describes an action they have no way to take.
                'Time and expenses recorded against matters, before they roll into a fee note.'}
          </p>
        </div>
        {/* Wraps, and each button keeps its NATURAL width below sm. `flex-1` divided the line
            equally regardless of the label, which on a 320px phone gave "New time entry" 142px
            for a label that will not go below 167px — so it hung 27px past the edge. `grow`
            hands out only the slack: the pair shares one line where it fits and stacks where it
            does not. */}
        <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:[&>*]:grow">
          {/* A pupil may READ this ledger but never write to it (activities.routes.ts WRITE_ROLES,
              pinned by pupil-access-boundary.guard.test.ts). Showing the buttons anyway walked
              them through a whole form whose save could only ever 403 — the same fault
              canCreateMatter was added to fix on the matter form. */}
          {canLogActivity && (
            <>
              <Button variant="outline" onClick={() => setCreateOpen('expense')} className="gap-1.5">
                <Receipt size={14} weight="duotone" /> New expense
              </Button>
              <Button onClick={() => setCreateOpen('time')} className="gap-1.5">
                <Plus size={14} weight="bold" /> New time entry
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Totals row */}
      {totalsQ.data && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard
            label="Total time"
            value={fmtDuration(totalsQ.data.totalMinutes)}
            sub={`${totalsQ.data.totalHours.toFixed(2)} hours`}
            icon={<Clock size={16} weight="duotone" />}
          />
          <KpiCard
            label="Billable"
            value={fmtMoney(totalsQ.data.billableAmount, totalsQ.data.currency)}
            sub={`${totalsQ.data.billableCount} item${totalsQ.data.billableCount === 1 ? '' : 's'}`}
            tone="positive"
          />
          <KpiCard
            label="Non-billable"
            value={fmtMoney(totalsQ.data.nonBillableAmount, totalsQ.data.currency)}
            sub={`${totalsQ.data.nonBillableCount} item${totalsQ.data.nonBillableCount === 1 ? '' : 's'}`}
            tone="muted"
          />
          <KpiCard
            label="Expenses"
            value={fmtMoney(totalsQ.data.expenseAmount, totalsQ.data.currency)}
            sub={`${totalsQ.data.expenseCount} item${totalsQ.data.expenseCount === 1 ? '' : 's'}`}
            icon={<Receipt size={16} weight="duotone" />}
          />
        </div>
      )}

      {/* Filters strip */}
      <Card className="px-4 py-3">
        {/* One row once there is room for it. The column count follows how many controls are
            actually drawn — five normally, six when the Firm user filter is shown — because a
            fixed four left the last date field stranded on a line of its own. */}
        <div
          className={`grid grid-cols-2 items-end gap-x-3 gap-y-3 sm:grid-cols-3 ${
            isBarrister ? 'lg:grid-cols-5' : 'lg:grid-cols-6'
          }`}
        >
          <div>
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Type
            </label>
            <select
              value={type ?? ''}
              onChange={(e) => { setType(e.target.value ? (e.target.value as ActivityType) : undefined); setPage(1); }}
              className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All</option>
              <option value="time">Time</option>
              <option value="expense">Expense</option>
            </select>
          </div>

          <div>
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Invoice status
            </label>
            <select
              value={invoiceStatus ?? ''}
              onChange={(e) => { setInvoiceStatus(e.target.value ? (e.target.value as ActivityInvoiceStatus) : undefined); setPage(1); }}
              className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All</option>
              <option value="unbilled">Unbilled</option>
              <option value="draft">Draft</option>
              <option value="billed">Billed</option>
              <option value="non_billable">Non-billable</option>
            </select>
          </div>

          {/* CATEGORY — the reason one spelling per category is enforced at all. Without this
              control a barrister is made to categorise every entry and can never ask what a
              category cost them. The server matches every stored spelling of the chosen
              category, so entries logged before that rule existed are counted here too. */}
          <div className="col-span-2 sm:col-span-1">
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              Category
            </label>
            <select
              value={category ?? ''}
              onChange={(e) => { setCategory(e.target.value || undefined); setPage(1); }}
              className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {!isBarrister && (
            <div>
              <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                Firm user
              </label>
              <select
                value={firmUserId ?? ''}
                onChange={(e) => { setFirmUserId(e.target.value || undefined); setPage(1); }}
                className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">Everyone</option>
                {currentUser && (
                  <option value={currentUser.id}>
                    {currentUser.firstName} {currentUser.lastName} (me)
                  </option>
                )}
                {usersQ.data
                  ?.filter((u) => u.id !== currentUser?.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              From
            </label>
            <input
              type="date"
              className="box-border block h-9 w-full min-w-0 max-w-full appearance-none rounded-md border border-input bg-background px-2 text-sm [&::-webkit-date-and-time-value]:text-left [&::-webkit-date-and-time-value]:leading-[1.5rem]"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            />
          </div>

          <div>
            <label className="mb-0.5 block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              To
            </label>
            <input
              type="date"
              className="box-border block h-9 w-full min-w-0 max-w-full appearance-none rounded-md border border-input bg-background px-2 text-sm [&::-webkit-date-and-time-value]:text-left [&::-webkit-date-and-time-value]:leading-[1.5rem]"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            />
          </div>

          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={clearFilters} className="col-span-2 justify-self-start sm:col-span-1 sm:self-end">
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* List */}
      <Card className="overflow-hidden">
        {listQ.isLoading && (
          <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
            <CircleNotch size={14} className="animate-spin" /> Loading activities…
          </div>
        )}
        {listQ.isError && (
          // A 403 here is a decision, not a fault, and the server's own words name internal
          // member types ("Route restricted to: admin, senior_clerk, …") — meaningless to the
          // person reading it and not something to print at them. Say plainly that it is
          // restricted; keep the real message for anything that IS a fault.
          <div className="px-5 py-6 text-sm text-muted-foreground">
            {isForbidden(listQ.error)
              ? 'Time & Expenses is not available for your role.'
              : getApiErrorMessage(listQ.error, 'Could not load activities.')}
          </div>
        )}
        {listQ.data && listQ.data.activities.length === 0 && (
          <div className="px-5 py-10 text-center">
            <Clock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" weight="duotone" />
            <div className="mb-1 text-sm font-medium">No activities yet</div>
            <p className="text-xs text-muted-foreground">
              Click <strong>New time entry</strong> or <strong>New expense</strong> to get started.
            </p>
          </div>
        )}
        {listQ.data && listQ.data.activities.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-[0.06em] text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Date</th>
                <th className="px-4 py-2 text-left font-medium">Type</th>
                <th className="px-4 py-2 text-left font-medium">Description</th>
                <th className="px-4 py-2 text-left font-medium">Matter</th>
                <th className="px-4 py-2 text-left font-medium">User</th>
                <th className="px-4 py-2 text-right font-medium">Qty</th>
                <th className="px-4 py-2 text-right font-medium">Amount</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {listQ.data.activities.map((a) => {
                const canEdit = a.firmUserId === currentUser?.id && a.invoiceStatus !== 'billed';
                return (
                  <tr key={a.id} className="hover:bg-muted/20">
                    <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                      {new Date(a.activityDate).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-4 py-2.5">
                      {a.entryType === 'time' ? (
                        <Badge variant="outline" className="gap-1">
                          <Clock size={11} weight="duotone" /> Time
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Receipt size={11} weight="duotone" /> Expense
                        </Badge>
                      )}
                    </td>
                    <td className="max-w-xs px-4 py-2.5">
                      <div className="truncate font-medium">{a.description}</div>
                      {a.category && (
                        // Normalised for display, as the category picker is. Rows written before
                        // the one-spelling rule hold whatever was typed ("travel"), and showing
                        // those raw put two spellings of one category on screen at once.
                        <div className="truncate text-xs text-muted-foreground">
                          {normaliseCategory(a.category)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[12.5px]">
                      {a.case ? (
                        <span title={a.case.caseName}>{a.case.caseNumber}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[12.5px]">
                      {a.firmUser.firstName} {a.firmUser.lastName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right text-[12.5px]">
                      {a.entryType === 'time'
                        ? a.durationMinutes != null
                          ? fmtDuration(a.durationMinutes)
                          : '—'
                        : a.quantity ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      {fmtMoney(Number(a.amount), a.currency)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statusBadgeClass(a.invoiceStatus)}`}
                      >
                        {statusLabel(a.invoiceStatus)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right">
                      <RowActions
                        className="justify-end"
                        onView={() => setViewTarget(a)}
                        viewLabel="View"
                        onEdit={() => setEditTarget(a)}
                        editLabel={canEdit ? 'Edit' : 'Cannot edit a billed entry, or one created by another user'}
                        disableEdit={!canEdit}
                        onDelete={() => deleteMut.mutate(a.id)}
                        deleteLabel={canEdit ? 'Delete' : 'Cannot delete a billed entry, or one created by another user'}
                        disableDelete={!canEdit}
                        deleteConfirm={{
                          title: 'Delete time entry?',
                          message: 'Delete this activity? This cannot be undone (until retention sweep).',
                          confirmLabel: 'Delete',
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {listQ.data && listQ.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-2.5 text-xs">
            <span className="text-muted-foreground">
              Page {listQ.data.pagination.page} of {listQ.data.pagination.totalPages} · {listQ.data.pagination.total} entries
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(listQ.data.pagination.totalPages, p + 1))}
                disabled={page >= listQ.data.pagination.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {createOpen && (
        <ActivityModal
          defaultType={createOpen}
          matterId={emailMatterId ?? undefined}
          onClose={() => { setCreateOpen(null); clearEmailParams(); }}
          onSaved={() => { setCreateOpen(null); clearEmailParams(); }}
        />
      )}
      {editTarget && (
        <ActivityModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
      {viewTarget && (
        <ActivityModal
          initial={viewTarget}
          readOnly
          onClose={() => setViewTarget(null)}
          onSaved={() => setViewTarget(null)}
        />
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  icon?: React.ReactNode;
  tone?: 'positive' | 'muted';
}) {
  return (
    <Card className="px-4 py-3">
      <div className="flex items-center justify-between text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
        <span>{label}</span>
        {icon}
      </div>
      <div
        className={`mt-1 text-xl font-semibold ${
          tone === 'positive'
            ? 'text-emerald-700 dark:text-emerald-300'
            : tone === 'muted'
            ? 'text-muted-foreground'
            : 'text-foreground'
        }`}
      >
        {value}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
    </Card>
  );
}
